import React, { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  useLensCollaborationMutation,
  useLensRevision,
  useLensRevisions,
} from '~/hooks/apis/reporting/useLensCollaboration';

import type { Lens, LensResponse } from '~/types/leLens';

import { editableLensDefinition, lensChangeLabel, lensDefinitionChanges } from './lensDefinitionChanges';
import { ownerDisplayName } from './lensLibrary';

export default function LensCollaborationDialog({
  lens,
  access,
  open,
  onClose,
}: {
  lens: Lens;
  access: LensResponse['access'];
  open: boolean;
  onClose: () => void;
}) {
  const [revision, setRevision] = useState<number | null>(null);
  const [notice, setNotice] = useState('');
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [revisionOffset, setRevisionOffset] = useState(0);
  const revisions = useLensRevisions(lens.id, open && access.canEdit, revisionOffset);
  const snapshot = useLensRevision(lens.id, revision);
  const mutation = useLensCollaborationMutation(lens.id);
  const current = { title: lens.title, description: lens.description, spec: lens.spec };
  const candidate = snapshot.data
    ? {
        title: snapshot.data.lens.title,
        description: snapshot.data.lens.description,
        spec: snapshot.data.lens.spec,
      }
    : undefined;
  const changes = candidate
    ? lensDefinitionChanges(editableLensDefinition(current), editableLensDefinition(candidate))
    : [];
  const failure = mutation.error ?? revisions.error ?? snapshot.error;
  const run = async (suffix: string, body: unknown) => {
    try {
      await mutation.mutateAsync({ suffix, body });
      setRevision(null);
      setConfirmRestore(false);
      setNotice('Change recorded.');
    } catch {
      setNotice('');
    }
  };
  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={() => !mutation.isPending && onClose()}>
      <DialogTitle sx={{ pb: 2 }}>
        Version history
        <Typography sx={{ fontSize: 13, opacity: 0.8, mt: 0.5 }}>{lens.title}</Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {notice && <Alert severity="success">{notice}</Alert>}
        {failure && (
          <Alert severity="error">
            {failure instanceof Error ? failure.message : 'Could not load version history.'}
          </Alert>
        )}
        {!access.canEdit ? (
          <Typography sx={{ p: 3 }}>Version history is available to people who can edit this Lens.</Typography>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px minmax(0, 1fr)' }, minHeight: 420 }}>
            <Box
              sx={{
                borderRight: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
                p: 2,
                maxHeight: 580,
                overflowY: 'auto',
              }}
            >
              <Typography color="text.secondary" sx={{ fontSize: 12, mb: 1 }}>
                SAVED VERSIONS
              </Typography>
              {revisions.isPending && <CircularProgress size={20} aria-label="Loading versions" />}
              {revisions.data?.revisions.length === 0 && (
                <Typography variant="body2">No saved versions yet.</Typography>
              )}
              <List disablePadding aria-label="Saved versions">
                {revisions.data?.revisions.map((item) => (
                  <ListItemButton
                    key={item.version}
                    selected={revision === item.version}
                    onClick={() => {
                      setRevision(item.version);
                      setConfirmRestore(false);
                    }}
                    sx={{ display: 'block', borderRadius: '8px', mb: 1, p: 1.5, alignItems: 'flex-start' }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Version {item.version}</Typography>
                      {item.version === lens.version && (
                        <Chip size="small" label="Current" sx={{ height: 20, fontSize: 10 }} />
                      )}
                    </Stack>
                    <Typography color="text.secondary" sx={{ fontSize: 11, mt: 0.5 }}>
                      {new Date(item.createdAt).toLocaleString('en-AU', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </Typography>
                    <Typography sx={{ fontSize: 12, mt: 0.5 }}>
                      {item.reason === 'baseline' ? 'Earlier editor not recorded' : ownerDisplayName(item.actorEmail)}
                    </Typography>
                    <Typography sx={{ fontSize: 12, mt: 1, lineHeight: 1.5 }}>
                      {item.summary?.join(' · ') ?? (item.reason === 'created' ? 'Created this Lens' : 'Saved version')}
                    </Typography>
                  </ListItemButton>
                ))}
              </List>
              {(revisionOffset > 0 || revisions.data?.nextOffset != null) && (
                <Stack direction="row">
                  <Button
                    size="small"
                    disabled={revisionOffset === 0}
                    onClick={() => {
                      setRevisionOffset(Math.max(0, revisionOffset - 100));
                      setRevision(null);
                    }}
                  >
                    Newer
                  </Button>
                  <Button
                    size="small"
                    disabled={revisions.data?.nextOffset == null}
                    onClick={() => {
                      setRevisionOffset(revisions.data!.nextOffset!);
                      setRevision(null);
                    }}
                  >
                    Older
                  </Button>
                </Stack>
              )}
            </Box>
            <Stack spacing={2} sx={{ p: 3, minWidth: 0, maxHeight: 580, overflowY: 'auto' }}>
              {revision === null ? (
                <Box sx={{ my: 'auto' }}>
                  <Typography sx={{ fontSize: 18, fontWeight: 600 }}>See how this Lens evolved</Typography>
                  <Typography color="text.secondary" sx={{ fontSize: 13, mt: 1 }}>
                    Choose a saved version to compare it with the current Lens.
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: 12, mt: 2 }}>
                    Summaries describe each save against the preceding recorded version. Data refreshes and page views
                    are not changes to the Lens.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
                    Version {revision} {revision === lens.version ? '· Current' : '→ Current'}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                    What is different between this saved version and today's Lens.
                  </Typography>
                  {snapshot.isPending && <CircularProgress size={20} aria-label="Loading version" />}
                  {candidate && (
                    <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
                      <Table size="small" sx={{ tableLayout: 'fixed', '& td': { fontSize: 12, verticalAlign: 'top' } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Setting</TableCell>
                            <TableCell>{`Version ${revision}`}</TableCell>
                            <TableCell>Current</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {changes.map((change) => (
                            <TableRow key={change.field}>
                              <TableCell title={change.field}>{lensChangeLabel(change.field, candidate.spec)}</TableCell>
                              <TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                {change.after}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                {change.before}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {changes.length === 0 && (
                        <Typography color="text.secondary" variant="body2">
                          This version has the same definition as the current Lens.
                        </Typography>
                      )}
                    </Box>
                  )}
                  {snapshot.data && changes.length > 0 && access.canEdit && revision !== lens.version && (
                    <>
                      <Divider />
                      <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                        Restoring creates a new saved version. The existing history is kept.
                      </Typography>
                      {confirmRestore ? (
                        <Alert
                          severity="warning"
                          action={
                            <Button
                              disabled={mutation.isPending}
                              onClick={() => void run(`revisions/${revision}/restore`, { baseVersion: lens.version })}
                            >
                              Confirm restore
                            </Button>
                          }
                        >
                          Restore version {revision} as the current Lens?
                          <Button size="small" onClick={() => setConfirmRestore(false)}>
                            Cancel
                          </Button>
                        </Alert>
                      ) : (
                        <Button
                          variant="outlined"
                          disabled={mutation.isPending}
                          onClick={() => setConfirmRestore(true)}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          Restore this version…
                        </Button>
                      )}
                    </>
                  )}
                </>
              )}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
